import { useState } from "react"

import { useTranslation } from "react-i18next"

import { TokenUserBalanceCard } from "features/token/components/TokenUserBalanceCard"
import { ModalEditUser } from "features/user/actions/EditUserProfile/ModalEditUser"

import { usePermissions } from "contexts/PermissionsContext"

import { PERMISSIONS } from "constants/permissions"

import { GetSingleUser } from "services/user/type"

import { Maybe } from "types/common"

import { formatFiatCurrency, parseAndFormatHistoricalUSD,UNAVAILABLE_DASH } from "util/formatter"

import { _currentTenant } from "config/featureFlags"

import { CommentsCard } from "../cards"
import { BalanceCard } from "../cards/BalanceCard"
import { UserInfoPersonalCard } from "../cards/UserInfoPersonalCard"
import { UserInfoPlayerCard } from "../cards/UserInfoPlayerCard"
import { UserPrivacyCard } from "../cards/UserPrivacyCard"
import { UserProfileHeader } from "../cards/UserProfileHeader"
import { UserTagsSection } from "../sections/UserTagsSection"
import { NgrTooltipContent } from "../tooltip/NgrTooltipContent"

import { UserTabContents } from "."

interface UserOverviewProps {
  userResult: Maybe<GetSingleUser>
  setEditBalance: (value: any) => void
  setOpenAddBonus: (value: boolean) => void
  setOpenSendMessage: (value: boolean) => void
  handleToggleExclusion: () => void
  updateKey: number
  handleAddBonusSuccess: () => void
  forceUpdate: () => void
  onLoad: () => void
  setUserResult: (value: any) => void
  handleDisable2FA: () => void
}

export const UserOverview = ({
  userResult,
  setEditBalance,
  setOpenAddBonus,
  setOpenSendMessage,
  handleToggleExclusion,
  updateKey,
  handleAddBonusSuccess,
  forceUpdate,
  onLoad,
  setUserResult,
  handleDisable2FA
}: UserOverviewProps) => {
  if (!userResult) return null
  const { has } = usePermissions()
  const { t } = useTranslation("translation", { useSuspense: false })
  // NGR tooltip is sensitive financial data - gate the same way as Dashboard.
  const canSeeGgrInsight = has(PERMISSIONS.GGR_INSIGHT_VIEW)
  const [openEditUser, setOpenEditUser] = useState(false)

  // In microservice mode the backend returns `null` balance/financial fields
  // (with these flags) when FS/bonus-service is down. Show "unavailable"
  // instead of rendering `null` as "$0" — a false zero reads as "money gone".
  const balanceUnavailable = userResult.balance_unavailable === true
  const financialsUnavailable = userResult.financials_unavailable === true

  const balanceCards = [
    {
      title: t("user.totalBalance"),
      value: balanceUnavailable ? UNAVAILABLE_DASH : parseAndFormatHistoricalUSD(userResult.balance ?? ""),
      type: "balance" as const,
      real: balanceUnavailable ? UNAVAILABLE_DASH : parseAndFormatHistoricalUSD(userResult.usd_balance ?? ""),
      bonus: balanceUnavailable ? UNAVAILABLE_DASH : parseAndFormatHistoricalUSD(userResult.busd_balance ?? ""),
      setEditBalance
    },
    {
      title: t("user.totalDeposits"),
      value: financialsUnavailable ? UNAVAILABLE_DASH : formatFiatCurrency(userResult.total_deposit_amount?.toString()),
      type: "deposits" as const,
      secondaryLabel: t("user.totalWithdrawals"),
      secondaryValue: financialsUnavailable ? UNAVAILABLE_DASH : formatFiatCurrency(userResult.total_withdrawal_amount?.toString())
    },
    {
      title: t("user.totalGgr"),
      value: financialsUnavailable ? UNAVAILABLE_DASH : formatFiatCurrency(String(userResult.total_ggr ?? 0)),
      type: "ggr" as const,
      secondaryLabel: t("user.totalNgr"),
      // total_ngr stays MNT-derived (not gated by financials_unavailable).
      secondaryValue:
        userResult.total_ngr !== undefined && userResult.total_ngr !== null ? formatFiatCurrency(String(userResult.total_ngr)) : "-",
      infoContent:
        canSeeGgrInsight && !financialsUnavailable ? (
          <NgrTooltipContent inputs={userResult.ngrInputs} wager={userResult.total_wager ?? 0} win={userResult.total_win ?? 0} />
        ) : undefined
    },
    {
      title: t("user.pnl"),
      value: financialsUnavailable ? UNAVAILABLE_DASH : formatFiatCurrency(userResult.total_pnl?.toString()),
      type: "pnl" as const
    }
  ]

  return (
    <div className="w-full flex flex-col sm:grid grid-col-1 gap-8">
      <UserProfileHeader
        userResult={userResult}
        handleToggleExclusion={handleToggleExclusion}
        updateKey={updateKey}
        handleAddBonusSuccess={handleAddBonusSuccess}
        setOpenAddBonus={setOpenAddBonus}
        setOpenSendMessage={setOpenSendMessage}
        onUpdate={onLoad}
      />
      {userResult?.id && <UserTagsSection userId={userResult.id} refreshKey={updateKey} />}
      {(balanceUnavailable || financialsUnavailable) && (
        <div className="rounded-md px-4 py-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30">
          {t("user.dataTemporarilyUnavailable")}
        </div>
      )}
      <div className="flex flex-col gap-4 md:flex-row">
        {balanceCards.map((card) => (
          <BalanceCard key={card.type} {...card} />
        ))}
        {/* Renders nothing when this tenant has no $TOKEN or the operator lacks
            PAGE_TOKEN — the gate is the server's, not a second tenant list here. */}
        <TokenUserBalanceCard tenant={_currentTenant ?? ""} userId={userResult.id} onAdjusted={onLoad} />
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <UserInfoPlayerCard user={userResult} setOpenEditUser={setOpenEditUser} />
        <UserInfoPersonalCard user={userResult} setOpenEditUser={setOpenEditUser} />
        {/* <UserInfoTechnicalCard user={userResult} /> */}

        <UserPrivacyCard user={userResult} handleDisable2FA={handleDisable2FA} />
      </div>
      <CommentsCard user={userResult} onSuccess={onLoad} />
      <div className="flex flex-row space-x-4">
        {/* <UserLastVisitCard user={userResult} /> */}
        {/* <UserRegistrationCard user={userResult} /> */}
      </div>
      {/* <div className="flex flex-row space-x-4">
        <UserGeneralCard user={userResult} />
        <UserWagerCard user={userResult} />
        <UserRakeBackCard user={userResult} />
        <UserBalanceCard user={userResult} reloadUser={onLoad} />
      </div> */}
      <UserTabContents
        forceUpdate={forceUpdate}
        updateKey={updateKey}
        userResult={userResult}
        onLoad={onLoad}
        setUserResult={setUserResult}
        gameSessions={userResult.gameSessions}
      />
      <ModalEditUser user={userResult} handler={() => setOpenEditUser(false)} open={openEditUser} onSuccess={onLoad} />
    </div>
  )
}
